import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';
import { ExplainTooltip, withInfo } from '../../../components/common/InfoTip';

export default function StockLogDataGrid({ transactions, loading, error, onClearError, showLotColumn = false, showLocationColumn = false, locations = [], batchMode = false }) {
  const { t } = useTranslation('inventory');

  const locationMap = Object.fromEntries(locations.map(l => [l.stock_location_id, l.name]));

  const columns = [
    {
      field: 'created_at',
      headerName: t('inventory:items.tabs.stock_log.columns.date'),
      flex: 1.2,
      renderCell: ({ value }) => (
        <span data-testid="stock-log-cell-date">
          <LocalizedDateDistance date={value} />
        </span>
      ),
    },
    withInfo({
      field: 'quantity_change',
      headerName: t('inventory:items.tabs.stock_log.columns.change'),
      flex: 0.8,
      renderCell: ({ value }) => (
        <Box data-testid="stock-log-cell-change" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {value >= 0
            ? <TrendingUpIcon fontSize="small" color="success" />
            : <TrendingDownIcon fontSize="small" color="error" />
          }
          <Typography
            variant="body2"
            color={value >= 0 ? 'success.main' : 'error.main'}
            fontWeight="medium"
          >
            {value >= 0 ? '+' : ''}{value}
          </Typography>
        </Box>
      ),
    }, t('inventory:help.stock_log.change')),
    withInfo({
      field: batchMode ? 'lot_quantity_after' : 'quantity_after',
      headerName: t(batchMode
        ? 'inventory:items.tabs.stock_log.columns.batch_stock_after'
        : 'inventory:items.tabs.stock_log.columns.stock_after'
      ),
      flex: 0.8,
      renderCell: ({ value }) => value != null
        ? <Typography data-testid="stock-log-cell-stock-after" variant="body2" fontWeight="medium">{value}</Typography>
        : <Typography data-testid="stock-log-cell-stock-after" variant="body2" color="text.secondary">—</Typography>,
    }, t(batchMode ? 'inventory:help.stock_log.batch_stock_after' : 'inventory:help.stock_log.stock_after')),
    withInfo({
      field: 'reason',
      headerName: t('inventory:items.tabs.stock_log.columns.reason'),
      flex: 1,
      // Each reason explains itself on hover
      renderCell: ({ value }) => (
        <ExplainTooltip title={t(`inventory:help.reasons.${value}`, { defaultValue: '' })}>
          <span data-testid="stock-log-cell-reason">{t(`inventory:items.stock_reasons.${value}`)}</span>
        </ExplainTooltip>
      ),
    }, t('inventory:help.stock_log.reason')),
    ...(showLotColumn ? [{
      field: 'lot_id',
      headerName: t('inventory:items.tabs.stock_log.columns.lot'),
      flex: 1,
      renderCell: ({ value }) => value
        ? <Typography data-testid="stock-log-cell-lot" variant="body2" sx={{ fontFamily: 'monospace' }}>{value.slice(0, 8)}…</Typography>
        : <Typography data-testid="stock-log-cell-lot" variant="body2" color="text.secondary">—</Typography>,
    }] : []),
    ...(showLocationColumn ? [withInfo({
      field: 'location_id',
      headerName: t('inventory:items.tabs.stock_log.columns.location'),
      flex: 1,
      renderCell: ({ value }) => value
        ? <Typography data-testid="stock-log-cell-location" variant="body2">{locationMap[value] ?? value.slice(0, 8) + '…'}</Typography>
        : <Typography data-testid="stock-log-cell-location" variant="body2" color="text.secondary">{t('inventory:items.no_location')}</Typography>,
    }, t('inventory:help.stock_log.location')), withInfo({
      field: batchMode ? 'lot_location_quantity_after' : 'location_quantity_after',
      headerName: t(batchMode
        ? 'inventory:items.tabs.stock_log.columns.batch_location_stock_after'
        : 'inventory:items.tabs.stock_log.columns.location_stock_after'
      ),
      flex: 0.8,
      renderCell: ({ value }) => value != null
        ? <Typography data-testid="stock-log-cell-location-stock-after" variant="body2" fontWeight="medium">{value}</Typography>
        : <Typography data-testid="stock-log-cell-location-stock-after" variant="body2" color="text.secondary">—</Typography>,
    }, t(batchMode ? 'inventory:help.stock_log.batch_location_stock_after' : 'inventory:help.stock_log.location_stock_after'))] : []),
    {
      field: 'notes',
      headerName: t('inventory:items.tabs.stock_log.columns.notes'),
      flex: 1.5,
      renderCell: ({ value }) => value
        ? <Typography data-testid="stock-log-cell-notes" variant="body2">{value}</Typography>
        : <Typography data-testid="stock-log-cell-notes" variant="body2" color="text.secondary">—</Typography>,
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flex="1" minHeight="0" flexDirection="column">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={onClearError}>{error}</Alert>
      )}
      {transactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('inventory:items.tabs.stock_log.empty')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DataGrid
            rows={transactions}
            columns={columns}
            getRowId={(row) => row.stock_transaction_id}
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{
              flex: 1,
              border: 'none',
              '& .MuiDataGrid-cell': { alignItems: 'center' },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
